import './App.css';
import { useEffect, useState, useCallback } from 'react';
import DashboardHeader from './dashboardheader.js';
import VerticalNavigation from './VerticalNavigation.js';
import bhcalc from './bhcalc.js';
import bhs from './bhs.js';

function DeleteCheckInModal({ checkIn, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await bhcalc.delete(`/check-in/${checkIn.id}`);
      onDeleted(checkIn.id);
      onClose();
    } catch {
      setError('Failed to delete check-in. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Delete Check-In</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
          </div>
          <p className="text-sm text-left text-gray-700 mb-4">
            Are you sure you want to delete the check-in for <strong>{checkIn.firstName} {checkIn.lastName}</strong>? This action cannot be undone.
          </p>
          {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckInFormModal({ checkIn, onClose, onSaved }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    partnerClientId: checkIn?.partnerClientId ?? '',
    firstName: checkIn?.firstName ?? '',
    lastName: checkIn?.lastName ?? '',
    email: checkIn?.email ?? '',
    gender: checkIn?.gender ?? '',
    age: checkIn?.age ?? '',
    heightFeet: checkIn?.heightFeet ?? '',
    heightInches: checkIn?.heightInches ?? '',
    weight: checkIn?.weight ?? '',
    abdomenInches: checkIn?.abdomenInches ?? '',
    bicepsInches: checkIn?.bicepsInches ?? '',
    thighInches: checkIn?.thighInches ?? '',
    calfInches: checkIn?.calfInches ?? '',
    wristInches: checkIn?.wristInches ?? '',
    hipDiameterInches: checkIn?.hipDiameterInches ?? '',
    cardioDays: checkIn?.cardioDays ?? '',
    cardioMinutes: checkIn?.cardioMinutes ?? '',
    strengthDays: checkIn?.strengthDays ?? '',
    strengthMinutes: checkIn?.strengthMinutes ?? '',
    mealsPerDay: checkIn?.mealsPerDay ?? '',
    snacksPerDay: checkIn?.snacksPerDay ?? '',
    timeOfFirstMeal: checkIn?.timeOfFirstMeal ?? '',
    timeOfLastMeal: checkIn?.timeOfLastMeal ?? '',
    isDiabetic: checkIn?.isDiabetic ?? false,
    hasPrediabetes: checkIn?.hasPrediabetes ?? false,
    hasDiabetes1: checkIn?.hasDiabetes1 ?? false,
    hasDiabetes2: checkIn?.hasDiabetes2 ?? false,
    hadBariatricSurgery: checkIn?.hadBariatricSurgery ?? false,
    typeOfBariatric: checkIn?.typeOfBariatric ?? '',
    isLactating: checkIn?.isLactating ?? false,
    notes: checkIn?.notes ?? '',
    readyForReview: checkIn?.readyForReview ?? false,
    reviewed: checkIn?.reviewed ?? false,
  });

  const isEditing = !!checkIn?.id;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (isEditing) {
        await bhcalc.put(`/check-in/${checkIn.id}`, formData);
      } else {
        await bhcalc.post('/check-in', formData);
      }
      onSaved();
    } catch {
      setError('Failed to save check-in. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? `Edit Check-In: ${checkIn.firstName} ${checkIn.lastName}` : 'New Check-In'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Personal Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter First Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter Last Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Health Details</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Age"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Height Feet</label>
                    <input
                      type="number"
                      name="heightFeet"
                      value={formData.heightFeet}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Height Inches</label>
                    <input
                      type="number"
                      name="heightInches"
                      value={formData.heightInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight in Pounds</label>
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="150"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Abdomen Measurement in Inches</label>
                    <input
                      type="number"
                      name="abdomenInches"
                      value={formData.abdomenInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dominant Biceps in Inches</label>
                    <input
                      type="number"
                      name="bicepsInches"
                      value={formData.bicepsInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dominant Thigh in Inches</label>
                    <input
                      type="number"
                      name="thighInches"
                      value={formData.thighInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dominant Calf in Inches</label>
                    <input
                      type="number"
                      name="calfInches"
                      value={formData.calfInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  {formData.gender === 'male' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Dominant Wrist Diameter in Inches</label>
                      <input
                        type="number"
                        name="wristInches"
                        value={formData.wristInches}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="9"
                      />
                    </div>
                  )}

                  {formData.gender === 'female' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hip Diameter in Inches</label>
                      <input
                        type="number"
                        name="hipDiameterInches"
                        value={formData.hipDiameterInches}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="38"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Activity</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cardio Sessions per Week</label>
                  <input
                    type="number"
                    name="cardioDays"
                    value={formData.cardioDays}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cardio Duration in Minutes per Session</label>
                  <input
                    type="number"
                    name="cardioMinutes"
                    value={formData.cardioMinutes}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Strength Training Sessions per Week</label>
                  <input
                    type="number"
                    name="strengthDays"
                    value={formData.strengthDays}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Strength Training Duration in Minutes per Session</label>
                  <input
                    type="number"
                    name="strengthMinutes"
                    value={formData.strengthMinutes}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="120"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Diet</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Meals per Day</label>
                  <input
                    type="number"
                    name="mealsPerDay"
                    value={formData.mealsPerDay}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Snacks per Day</label>
                  <input
                    type="number"
                    name="snacksPerDay"
                    value={formData.snacksPerDay}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Average Time of First Meal (24-hr Clock)</label>
                  <input
                    type="text"
                    name="timeOfFirstMeal"
                    value={formData.timeOfFirstMeal}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0830"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Average Time of Last Meal (24-hr Clock)</label>
                  <input
                    type="text"
                    name="timeOfLastMeal"
                    value={formData.timeOfLastMeal}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1830"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Conditions</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="isDiabetic"
                    checked={formData.isDiabetic || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Is Client Pre-Diabetic or Diabetic?</span>
                </label>

                {formData.isDiabetic && (
                  <div className="ml-4 space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hasPrediabetes"
                        checked={formData.hasPrediabetes || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Prediabetes</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hasDiabetes1"
                        checked={formData.hasDiabetes1 || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Diabetes Type 1</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hasDiabetes2"
                        checked={formData.hasDiabetes2 || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Diabetes Type 2</span>
                    </label>
                  </div>
                )}

                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="hadBariatricSurgery"
                    checked={formData.hadBariatricSurgery || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Has Client had Bariatric Surgery?</span>
                </label>

                {formData.hadBariatricSurgery && (
                  <div className="ml-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type of Bariatric Surgery</label>
                    <input
                      type="text"
                      name="typeOfBariatric"
                      value={formData.typeOfBariatric}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Lap-band, Balloon, Gastric Bypass, Other"
                    />
                  </div>
                )}

                {formData.gender === 'female' && (
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      name="isLactating"
                      checked={formData.isLactating || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span>Is Client Lactating?</span>
                  </label>
                )}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Additional</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any additional information..."
                />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Review Status</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="readyForReview"
                    checked={formData.readyForReview || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Ready for Review</span>
                </label>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="reviewed"
                    checked={formData.reviewed || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Reviewed</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Check-In'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CheckIns() {
  const userRole = bhs(state => state.userRole);
  const isCoach = userRole === 'coach';
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkInToManage, setCheckInToManage] = useState(null);
  const [checkInToDelete, setCheckInToDelete] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);

  const fetchCheckIns = useCallback(() => {
    setLoading(true);
    bhcalc.get('/check-in')
      .then(res => setCheckIns(res.data))
      .catch(() => setError('Failed to load check-ins.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCheckIns();
  }, [fetchCheckIns]);

  const handleDeleted = (id) => {
    setCheckIns(prev => prev.filter(c => c.id !== id));
  };

  const handleSaved = () => {
    setCheckInToManage(null);
    setShowNewForm(false);
    fetchCheckIns();
  };

  return (
    <div className="App min-h-screen">
      {(checkInToManage) && (
        <CheckInFormModal
          checkIn={checkInToManage}
          onClose={() => setCheckInToManage(null)}
          onSaved={handleSaved}
        />
      )}

      {showNewForm && (
        <CheckInFormModal
          checkIn={null}
          onClose={() => setShowNewForm(false)}
          onSaved={handleSaved}
        />
      )}

      {checkInToDelete && (
        <DeleteCheckInModal
          checkIn={checkInToDelete}
          onClose={() => setCheckInToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}

      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-left">Check-Ins</h2>
              <button
                onClick={() => setShowNewForm(true)}
                className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <span className="text-xl">+</span>
                <span>New Check-In</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Coach</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Ready for Review</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Reviewed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">Loading check-ins...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-red-600">{error}</td>
                    </tr>
                  ) : checkIns.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No check-ins found.</td>
                    </tr>
                  ) : (
                    checkIns.map(ci => (
                      <tr key={ci.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ci.firstName} {ci.lastName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{ci.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{ci.coach ? `${ci.coach.firstName} ${ci.coach.lastName}` : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${ci.readyForReview ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                            {ci.readyForReview ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${ci.reviewed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {ci.reviewed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(ci.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button onClick={() => setCheckInToManage(ci)} className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                          {!isCoach && (
                            <button onClick={() => setCheckInToDelete(ci)} className="text-red-600 hover:text-red-900">Delete</button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckIns;
